import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Link from '@mui/material/Link'
import Table from '@mui/material/Table'
import TableHead from '@mui/material/TableHead'
import TableBody from '@mui/material/TableBody'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import LinearProgress from '@mui/material/LinearProgress'
import Alert from '@mui/material/Alert'
import VisibilityIcon from '@mui/icons-material/Visibility'
import { useMenemosineDetailQuery } from './hooks/useMenemosineQuery'

function MenemosineDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { audit, versions, isLoading, isError } = useMenemosineDetailQuery(id)
  const [details, setDetails] = useState(null)

  const showEmptyState = !isLoading && !isError && versions.length === 0

  function handleOpenDetails(data) {
    setDetails(data)
  }

  function handleCloseDetails() {
    setDetails(null)
  }

  function handleBack() {
    navigate(-1)
  }

  return (
    <Box>
      <Typography variant="h5" component="h2" gutterBottom>
        Detalhamento das versoes
      </Typography>

      <Box sx={{ mb: 2 }}>
        <Link component="button" type="button" onClick={handleBack}>
          Voltar
        </Link>
      </Box>

      {isLoading && <LinearProgress sx={{ mb: 2 }} />}

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Erro ao buscar os detalhes da auditoria.
        </Alert>
      )}

      {audit && (
        <Paper sx={{ p: 2, mb: 3 }} variant="outlined">
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} sx={{ flexWrap: 'wrap' }}>
            <Typography variant="body2">
              <strong>Entidade:</strong> {audit.entity}
            </Typography>
            <Typography variant="body2">
              <strong>ID:</strong> {audit.objectId}
            </Typography>
            <Typography variant="body2">
              <strong>Tenant:</strong> {audit.tenantId}
            </Typography>
            <Typography variant="body2">
              <strong>Namespace:</strong> {audit.namespace}
            </Typography>
            <Typography variant="body2">
              <strong>Quantidade de versões:</strong> {audit.versionsCount}
            </Typography>
          </Stack>
        </Paper>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Versão</TableCell>
              <TableCell>Data da alteração</TableCell>
              <TableCell>Alterado por</TableCell>
              <TableCell>Detalhes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {showEmptyState && (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  Nenhum registro de ação encontrado
                </TableCell>
              </TableRow>
            )}

            {!isLoading &&
              versions.map((version, index) => (
                <TableRow key={`${version.version}-${index}`}>
                  <TableCell>{version.version}</TableCell>
                  <TableCell>{version.createdAt}</TableCell>
                  <TableCell>{version.userNameChanged || version.userIdChanged}</TableCell>
                  <TableCell>
                    <Tooltip title="Visualizar">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleOpenDetails(version.data)}
                        aria-label="Visualizar"
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={details !== null} onClose={handleCloseDetails} maxWidth="sm" fullWidth>
        <DialogTitle>Dados alterados</DialogTitle>
        <DialogContent>
          <Typography component="p" sx={{ wordBreak: 'break-word' }}>
            {details}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetails}>Cancelar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default MenemosineDetailPage
